import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { BaseFormData } from 'pages/reporting/shared/types'
import { useCallback } from 'react'
import { SignatureData, SignoffRole, SignoffState } from 'types'

interface UseSignoffHandlerProps<TFormData extends BaseFormData> {
  metadataEntryId: string
  formData: TFormData
  updateFormData: (data: Partial<TFormData>) => void | Promise<void>
  setIsSubmitting: (value: boolean) => void
  handlePublishRevision: (dataToUse: TFormData) => Promise<void>
}

interface UseSignoffHandlerResult {
  handleSignoff: (signatureData: SignatureData) => Promise<void>
}

export const useSignoffHandler = <TFormData extends BaseFormData>({
  metadataEntryId,
  formData,
  updateFormData,
  setIsSubmitting,
  handlePublishRevision,
}: UseSignoffHandlerProps<TFormData>): UseSignoffHandlerResult => {
  const { rolodexPatch } = useRolodexEdit()

  const handleSignoff = useCallback(
    async (signatureData: SignatureData): Promise<void> => {
      if (!metadataEntryId) {
        message.error('No metadata entry found')
        throw new Error('No metadata entry found')
      }

      try {
        setIsSubmitting(true)

        const roleLabel =
          signatureData.role === SignoffRole.REVIEWER
            ? 'Reviewer'
            : signatureData.role === SignoffRole.DATA_ANALYST
              ? 'Data Analyst'
              : 'Supervisor'
        message.loading({
          content: `Processing ${roleLabel.toLowerCase()} signoff...`,
          key: 'signoff',
          duration: 0,
        })

        const signoffUpdate =
          signatureData.role === SignoffRole.REVIEWER
            ? { reviewerSignoff: signatureData }
            : signatureData.role === SignoffRole.DATA_ANALYST
              ? { dataAnalystSignoff: signatureData }
              : { supervisorSignoff: signatureData }

        const updatedSignoff: SignoffState = {
          ...(formData.signoff || { lastRevision: 0 }),
          ...signoffUpdate,
        }

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: { signoff: updatedSignoff },
          },
        ])

        await updateFormData({ signoff: updatedSignoff } as Partial<TFormData>)

        const hasAllSignatures =
          updatedSignoff.supervisorSignoff &&
          updatedSignoff.dataAnalystSignoff &&
          updatedSignoff.reviewerSignoff

        if (hasAllSignatures) {
          const updatedFormData = {
            ...formData,
            signoff: updatedSignoff,
          }

          message.loading({
            content: 'Starting publish process...',
            key: 'signoff',
            duration: 0,
          })

          await handlePublishRevision(updatedFormData)

          message.success({
            content: 'All signatures complete and revision published',
            key: 'signoff',
          })
        } else {
          message.success({
            content: `${roleLabel} signoff completed`,
            key: 'signoff',
          })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to complete signoff'
        console.error('Failed to complete signoff:', error)
        message.error({
          content: errorMessage,
          key: 'signoff',
        })
        throw error
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      metadataEntryId,
      formData,
      updateFormData,
      rolodexPatch,
      handlePublishRevision,
      setIsSubmitting,
    ]
  )

  return { handleSignoff }
}
